import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import RemoveIcon from '@mui/icons-material/Remove';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import HistoryIcon from '@mui/icons-material/History';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import PersonIcon from '@mui/icons-material/Person';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import RestaurantMenuIcon from '@mui/icons-material/RestaurantMenu';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import NuIcon from '../components/NuIcon';
import { useAppState } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import * as salesService from '../services/salesService';
import { addActivity } from '../services/activityService';
import { getCouponValue, splitCartByCouponValue } from '../utils/couponSaleSplit';
import SaleAnnotations, { balanceAfterCouponNote } from '../components/SaleAnnotations';
import * as customerCouponService from '../services/customerCouponService';
import { formatCustomerLabel, formatCustomerSaleName } from '../services/customerService';

function roundMoney(value) {
  return Math.round((Number(value) || 0) * 100) / 100;
}

function getSaleType(sale) {
  if (sale?.paymentType === 'cash') return 'cash';
  if (sale?.paymentType === 'coupon' || sale?.couponId || sale?.couponName) return 'coupon';
  return 'credit';
}

function saleTypeLabel(type) {
  if (type === 'cash') return 'Cash';
  if (type === 'coupon') return 'Coupon';
  return 'Credit';
}

function SectionHeader({ icon, title, subtitle }) {
  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        {icon}
        <Box>
          <Typography variant="subtitle1" fontWeight={700}>
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="caption" color="text.secondary">
              {subtitle}
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
}

function Sales() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const salesView = searchParams.get('tab') === 'history' ? 'history' : 'sales';
  const { customers, meals, sales, coupons, customerCoupons, refreshData } = useAppState();
  const { currentUser, couponsEnabled } = useAuth();
  const [customer, setCustomer] = useState(null);
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [saleDept, setSaleDept] = useState('');
  const [groupCouponMode, setGroupCouponMode] = useState(false);
  const [cart, setCart] = useState([]);
  const [selectedCouponId, setSelectedCouponId] = useState('');
  const [applyCoupon, setApplyCoupon] = useState(false);
  // Local calendar date (not UTC) so filters match the user's current day
  const getTodayLocal = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };
  const [saleDate, setSaleDate] = useState(getTodayLocal);
  const [historyDate, setHistoryDate] = useState(getTodayLocal);
  const [historySearch, setHistorySearch] = useState('');
  const [historySaleType, setHistorySaleType] = useState('credit');
  const [historySelectedIds, setHistorySelectedIds] = useState([]);
  const [historyBulkDeleting, setHistoryBulkDeleting] = useState(false);
  const [mealSearch, setMealSearch] = useState('');
  const [selectedSale, setSelectedSale] = useState(null);
  const [selectedItemIndices, setSelectedItemIndices] = useState(new Set());
  const [recordingSale, setRecordingSale] = useState(false);
  const [saleSuccess, setSaleSuccess] = useState(null);
  const [salePanelTab, setSalePanelTab] = useState(0); // 0 = meals, 1 = coupon types
  const [assignCouponTypeIds, setAssignCouponTypeIds] = useState([]);
  const [assigningCoupon, setAssigningCoupon] = useState(false);
  const [addCouponSaleOpen, setAddCouponSaleOpen] = useState(false);
  const [addCouponSaleTypeId, setAddCouponSaleTypeId] = useState('');
  const [addCouponSaleSaving, setAddCouponSaleSaving] = useState(false);
  const [addCouponSaleError, setAddCouponSaleError] = useState('');

  useEffect(() => {
    if (!couponsEnabled) {
      setSalePanelTab(0);
      setGroupCouponMode(false);
      setAssignCouponTypeIds([]);
      setSelectedCouponId('');
      setApplyCoupon(false);
      setAddCouponSaleOpen(false);
      if (historySaleType === 'coupon') setHistorySaleType('credit');
    }
  }, [couponsEnabled]); // eslint-disable-line react-hooks/exhaustive-deps

  const activeMeals = meals.filter((m) => m.isActive !== false);

  const mealOptions = useMemo(() => {
    if (!mealSearch.trim()) return activeMeals;
    const q = mealSearch.toLowerCase().trim();
    return activeMeals.filter(
      (m) =>
        (m.name || '').toLowerCase().includes(q) ||
        (m.category || '').toLowerCase().includes(q)
    );
  }, [activeMeals, mealSearch]);

  const mealsByCategory = useMemo(() => {
    const groups = {};
    mealOptions.forEach((meal) => {
      const cat = meal.category || 'General';
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(meal);
    });
    return Object.entries(groups).sort(([a], [b]) => a.localeCompare(b));
  }, [mealOptions]);

  const activeCoupons = useMemo(
    () => (coupons || []).filter((c) => c.isActive !== false),
    [coupons]
  );

  const departments = useMemo(() => {
    const set = new Set();
    (customers || []).forEach((c) => {
      const d = (c.department || '').trim();
      if (d) set.add(d);
    });
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [customers]);

  const customersForSale = useMemo(() => {
    if (!saleDept) return customers || [];
    return (customers || []).filter((c) => (c.department || '').trim() === saleDept);
  }, [customers, saleDept]);

  const unusedForCustomer = useMemo(() => {
    if (!customer?.id) return [];
    return (customerCoupons || []).filter(
      (cc) => cc.customerId === customer.id && roundMoney(cc.remainingValue) > 0
    );
  }, [customerCoupons, customer]);

  const couponTypeKey = (cc) => {
    if (cc?.couponId) return `id:${cc.couponId}`;
    return `name:${(cc?.couponName || '').trim().toLowerCase()}`;
  };

  /** Group same coupon type for display (name + count + total remaining). */
  const unusedCouponGroups = useMemo(() => {
    const map = new Map();
    unusedForCustomer.forEach((cc) => {
      const key = couponTypeKey(cc);
      if (!map.has(key)) {
        map.set(key, {
          key,
          couponId: cc.couponId || null,
          couponName: cc.couponName || 'Coupon',
          quantity: cc.quantity,
          rate: cc.rate,
          count: 0,
          remainingValue: 0,
          entries: [],
        });
      }
      const g = map.get(key);
      g.count += 1;
      g.remainingValue = roundMoney(g.remainingValue + roundMoney(cc.remainingValue));
      g.entries.push(cc);
    });
    return [...map.values()].sort((a, b) => a.couponName.localeCompare(b.couponName));
  }, [unusedForCustomer]);

  const couponCountByCustomerId = useMemo(() => {
    const map = {};
    (customerCoupons || []).forEach((cc) => {
      if (roundMoney(cc.remainingValue) <= 0) return;
      map[cc.customerId] = (map[cc.customerId] || 0) + 1;
    });
    return map;
  }, [customerCoupons]);

  const couponBalanceByCustomerId = useMemo(() => {
    const map = {};
    (customerCoupons || []).forEach((cc) => {
      const rem = roundMoney(cc.remainingValue);
      if (rem <= 0) return;
      map[cc.customerId] = roundMoney((map[cc.customerId] || 0) + rem);
    });
    return map;
  }, [customerCoupons]);

  const formatCustomerOption = (c) => {
    if (!c) return '';
    const base = formatCustomerLabel(c);
    if (!couponsEnabled) return base;
    const count = couponCountByCustomerId[c.id] || 0;
    return count > 0 ? `${base} · ${count} coupon${count === 1 ? '' : 's'}` : base;
  };

  const assignCouponTypes = useMemo(
    () => activeCoupons.filter((c) => assignCouponTypeIds.includes(c.id)),
    [activeCoupons, assignCouponTypeIds]
  );

  const toggleAssignCouponType = (id) => {
    setAssignCouponTypeIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleSelectAllAssignCouponTypes = (checked) => {
    setAssignCouponTypeIds(checked ? activeCoupons.map((c) => c.id) : []);
  };

  /** Coupon pick value: group:<key> (grouped customer coupons) or catalog:<id> */
  const selectedWalletGroup = useMemo(() => {
    if (!selectedCouponId.startsWith('group:')) return null;
    const key = selectedCouponId.slice('group:'.length);
    return unusedCouponGroups.find((g) => g.key === key) || null;
  }, [selectedCouponId, unusedCouponGroups]);

  const selectedCatalogCoupon = useMemo(() => {
    if (selectedCouponId.startsWith('group:') || selectedCouponId.startsWith('wallet:')) {
      return null;
    }
    const id = selectedCouponId.startsWith('catalog:')
      ? selectedCouponId.slice('catalog:'.length)
      : selectedCouponId;
    if (!id) return null;
    return activeCoupons.find((c) => c.id === id) || null;
  }, [selectedCouponId, activeCoupons]);

  const selectedCoupon = useMemo(() => {
    if (selectedWalletGroup) {
      return {
        id: selectedWalletGroup.couponId || selectedWalletGroup.key,
        name: selectedWalletGroup.couponName,
        quantity: selectedWalletGroup.quantity,
        rate: selectedWalletGroup.rate,
        remainingValue: selectedWalletGroup.remainingValue,
        count: selectedWalletGroup.count,
        customerCouponId: selectedWalletGroup.entries[0]?.id || null,
        entries: selectedWalletGroup.entries,
        isWallet: true,
      };
    }
    if (selectedCatalogCoupon) {
      return { ...selectedCatalogCoupon, isWallet: false, customerCouponId: null, entries: [] };
    }
    return null;
  }, [selectedWalletGroup, selectedCatalogCoupon]);

  const couponApplied = applyCoupon && !!selectedCoupon;

  // Clear selection if the chosen coupon was disabled / redeemed.
  useEffect(() => {
    if (selectedCouponId && !selectedCoupon) {
      setSelectedCouponId('');
      setApplyCoupon(false);
    }
  }, [selectedCouponId, selectedCoupon]);

  useEffect(() => {
    if (groupCouponMode) {
      setCustomer(null);
      setSelectedCouponId((prev) =>
        prev.startsWith('group:') || prev.startsWith('wallet:') ? '' : prev
      );
    } else {
      setSelectedCustomers([]);
    }
  }, [groupCouponMode]);

  useEffect(() => {
    // Drop customers outside filtered department
    if (saleDept) {
      setSelectedCustomers((prev) =>
        prev.filter((c) => (c.department || '').trim() === saleDept)
      );
      if (customer && (customer.department || '').trim() !== saleDept) {
        setCustomer(null);
      }
    }
  }, [saleDept]); // eslint-disable-line react-hooks/exhaustive-deps

  // When customer changes, clear wallet/group coupon if it belongs to someone else
  useEffect(() => {
    if (!selectedCouponId.startsWith('group:') && !selectedCouponId.startsWith('wallet:')) return;
    if (!customer?.id) {
      setSelectedCouponId('');
      setApplyCoupon(false);
      return;
    }
    if (selectedCouponId.startsWith('group:') && !selectedWalletGroup) {
      setSelectedCouponId('');
      setApplyCoupon(false);
    }
  }, [customer, selectedCouponId, selectedWalletGroup]);

  const filteredSales = useMemo(() => {
    const q = historySearch.toLowerCase().trim();
    const list = sales.filter((s) => {
      if (getSaleType(s) !== historySaleType) return false;
      if (historyDate && s.date !== historyDate) return false;
      if (!q) return true;
      const customerName = (s.customerName || '').toLowerCase();
      const couponName = (s.couponName || '').toLowerCase();
      const dateStr = (s.date || '').toLowerCase();
      const totalStr = String(s.totalAmount ?? '');
      return (
        customerName.includes(q) ||
        couponName.includes(q) ||
        dateStr.includes(q) ||
        totalStr.includes(q)
      );
    });
    return [...list].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  }, [sales, historyDate, historySearch, historySaleType]);

  const historySummary = useMemo(() => {
    const total = filteredSales.reduce((sum, s) => sum + (s.totalAmount || 0), 0);
    return { count: filteredSales.length, total };
  }, [filteredSales]);

  const historySelectedSales = useMemo(
    () => filteredSales.filter((s) => historySelectedIds.includes(s.id)),
    [filteredSales, historySelectedIds]
  );

  const historySelectedTotal = useMemo(
    () => historySelectedSales.reduce((sum, s) => sum + (s.totalAmount || 0), 0),
    [historySelectedSales]
  );

  useEffect(() => {
    setHistorySelectedIds([]);
  }, [historyDate, historySearch, historySaleType, salesView]);

  // Drop selections that are no longer in the filtered list
  useEffect(() => {
    const visible = new Set(filteredSales.map((s) => s.id));
    setHistorySelectedIds((prev) => {
      const next = prev.filter((id) => visible.has(id));
      return next.length === prev.length ? prev : next;
    });
  }, [filteredSales]);

  const handleHistorySelectAll = (e) => {
    if (e.target.checked) {
      setHistorySelectedIds(filteredSales.map((s) => s.id));
    } else {
      setHistorySelectedIds([]);
    }
  };

  const handleHistorySelectOne = (id) => {
    setHistorySelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const addToCart = (meal) => {
    const existing = cart.find((c) => c.mealId === meal.id);
    const price = Number(meal.price) || 0;
    if (existing) {
      const newQty = existing.quantity + 0.5;
      setCart(
        cart.map((c) =>
          c.mealId === meal.id
            ? { ...c, quantity: newQty, subtotal: Math.round(newQty * price * 100) / 100 }
            : c
        )
      );
    } else {
      setCart([
        ...cart,
        {
          mealId: meal.id,
          mealName: meal.name,
          quantity: 1,
          unitPrice: price,
          subtotal: Math.round(1 * price * 100) / 100,
        },
      ]);
    }
  };

  const adjustQuantity = (mealId, delta) => {
    const item = cart.find((c) => c.mealId === mealId);
    if (!item) return;
    const newQty = Math.round((item.quantity + delta) * 2) / 2;
    if (newQty < 0.5) {
      setCart(cart.filter((c) => c.mealId !== mealId));
      return;
    }
    setCart(
      cart.map((c) =>
        c.mealId === mealId
          ? { ...c, quantity: newQty, subtotal: Math.round(newQty * c.unitPrice * 100) / 100 }
          : c
      )
    );
  };

  const updateQuantity = (mealId, qty) => {
    const num = Number(qty);
    if (num < 0.5 || isNaN(num)) {
      setCart(cart.filter((c) => c.mealId !== mealId));
      return;
    }
    setCart(
      cart.map((c) =>
        c.mealId === mealId
          ? { ...c, quantity: num, subtotal: Math.round(num * c.unitPrice * 100) / 100 }
          : c
      )
    );
  };

  /** Edit line price (subtotal); keep unitPrice in sync for qty changes. */
  const updateLinePrice = (mealId, price) => {
    const num = Number(price);
    if (isNaN(num) || num < 0) return;
    const subtotal = Math.round(num * 100) / 100;
    setCart(
      cart.map((c) => {
        if (c.mealId !== mealId) return c;
        const qty = Number(c.quantity) || 1;
        const unitPrice = qty > 0 ? Math.round((subtotal / qty) * 100) / 100 : subtotal;
        return { ...c, subtotal, unitPrice };
      })
    );
  };

  const totalAmount = cart.reduce((sum, c) => sum + c.subtotal, 0);

  const couponValue = useMemo(() => {
    if (!couponApplied || !selectedCoupon) return 0;
    if (selectedCoupon.isWallet) {
      return roundMoney(selectedCoupon.remainingValue);
    }
    return getCouponValue(selectedCoupon);
  }, [couponApplied, selectedCoupon]);

  const couponSplit = useMemo(() => {
    if (!couponApplied || cart.length === 0) {
      return { couponTotal: 0, creditTotal: 0, hasCreditBalance: false };
    }
    const split = splitCartByCouponValue(cart, couponValue);
    return {
      ...split,
      hasCreditBalance: split.creditTotal > 0,
    };
  }, [cart, couponApplied, couponValue]);

  const findWalletGroupForCustomer = (cust, preferredCoupon) => {
    if (!cust?.id) return null;
    const unused = (customerCoupons || []).filter(
      (cc) => cc.customerId === cust.id && roundMoney(cc.remainingValue) > 0
    );
    if (unused.length === 0) return null;

    const buildGroup = (entries) => {
      if (!entries.length) return null;
      const first = entries[0];
      return {
        couponId: first.couponId || null,
        couponName: first.couponName || 'Coupon',
        quantity: first.quantity,
        rate: first.rate,
        count: entries.length,
        remainingValue: roundMoney(
          entries.reduce((s, e) => s + roundMoney(e.remainingValue), 0)
        ),
        entries,
        id: first.id,
      };
    };

    if (preferredCoupon) {
      const preferredId = preferredCoupon.couponId || preferredCoupon.id;
      const preferredName = (preferredCoupon.couponName || preferredCoupon.name || '')
        .trim()
        .toLowerCase();
      const byId = preferredId
        ? unused.filter((cc) => cc.couponId && cc.couponId === preferredId)
        : [];
      if (byId.length) return buildGroup(byId);
      const byName = preferredName
        ? unused.filter((cc) => (cc.couponName || '').trim().toLowerCase() === preferredName)
        : [];
      if (byName.length) return buildGroup(byName);
    }

    // Default: first type group
    const firstKey = couponTypeKey(unused[0]);
    return buildGroup(unused.filter((cc) => couponTypeKey(cc) === firstKey));
  };

  const handleRemoveItemFromSale = async (sale, itemIndex) => {
    const newItems = (sale.items || []).filter((_, i) => i !== itemIndex);
    if (newItems.length === 0) {
      if (window.confirm('Remove the last item? This will delete the entire sale.')) {
        await salesService.deleteSale(sale.id);
        addActivity(currentUser?.userId || 'Unknown', `Deleted sale (${sale.customerName})`);
        setSelectedSale(null);
        setSelectedItemIndices(new Set());
        refreshData();
      }
      return;
    }
    const newTotal = newItems.reduce((s, i) => s + (i.subtotal || 0), 0);
    await salesService.updateSale(sale.id, { items: newItems });
    addActivity(currentUser?.userId || 'Unknown', `Removed item from sale: ${sale.customerName}`);
    setSelectedSale((prev) =>
      prev?.id === sale.id ? { ...prev, items: newItems, totalAmount: newTotal } : prev
    );
    setSelectedItemIndices(new Set());
    refreshData();
  };

  const handleRemoveMultipleItemsFromSale = async () => {
    if (!selectedSale || selectedItemIndices.size === 0) return;
    const indices = Array.from(selectedItemIndices);
    const newItems = (selectedSale.items || []).filter((_, i) => !indices.includes(i));
    if (newItems.length === 0) {
      if (window.confirm('Remove all selected items? This will delete the entire sale.')) {
        await salesService.deleteSale(selectedSale.id);
        addActivity(currentUser?.userId || 'Unknown', `Deleted sale (${selectedSale.customerName})`);
        setSelectedSale(null);
        setSelectedItemIndices(new Set());
        refreshData();
      }
      return;
    }
    const newTotal = newItems.reduce((s, i) => s + (i.subtotal || 0), 0);
    await salesService.updateSale(selectedSale.id, { items: newItems });
    addActivity(
      currentUser?.userId || 'Unknown',
      `Removed ${indices.length} item(s) from sale: ${selectedSale.customerName}`
    );
    setSelectedSale((prev) =>
      prev?.id === selectedSale.id ? { ...prev, items: newItems, totalAmount: newTotal } : prev
    );
    setSelectedItemIndices(new Set());
    refreshData();
  };

  const toggleItemSelection = (idx) => {
    setSelectedItemIndices((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  const toggleSelectAllItems = () => {
    const items = selectedSale?.items || [];
    if (selectedItemIndices.size === items.length) {
      setSelectedItemIndices(new Set());
    } else {
      setSelectedItemIndices(new Set(items.map((_, i) => i)));
    }
  };

  const handleAssignCoupon = async () => {
    if (assigningCoupon || recordingSale) return;
    if (assignCouponTypes.length === 0) {
      window.alert('Select one or more coupon types from the Coupon Types tab.');
      setSalePanelTab(1);
      return;
    }
    const targets = groupCouponMode ? selectedCustomers : customer ? [customer] : [];
    if (targets.length === 0) {
      window.alert(
        groupCouponMode
          ? 'Select at least one customer to assign the coupon.'
          : 'Select a customer to assign the coupon.'
      );
      return;
    }
    setAssigningCoupon(true);
    try {
      const customerIds = targets.map((c) => c.id);
      for (const type of assignCouponTypes) {
        await customerCouponService.assignCouponsToCustomers({
          customerIds,
          coupon: type,
        });
        addActivity(
          currentUser?.userId || 'Unknown',
          `Assigned coupon "${type.name}" to ${targets.length} customer(s)`
        );
      }
      refreshData();
      const summary = assignCouponTypes
        .map((t) => `"${t.name}" (Nu ${getCouponValue(t).toFixed(2)})`)
        .join(', ');
      window.alert(
        `Assigned ${assignCouponTypes.length} coupon type${assignCouponTypes.length === 1 ? '' : 's'} to ${targets.length} customer${targets.length === 1 ? '' : 's'}: ${summary}.`
      );
      setAssignCouponTypeIds([]);
    } catch (err) {
      console.error('Failed to assign coupon:', err);
      window.alert(err.message || 'Failed to assign coupon.');
    } finally {
      setAssigningCoupon(false);
    }
  };

  const customersNeedingCoupon = useMemo(() => {
    const hasUnused = (custId) =>
      (customerCoupons || []).some(
        (cc) => cc.customerId === custId && roundMoney(cc.remainingValue) > 0
      );
    if (groupCouponMode) {
      return selectedCustomers.filter((c) => !hasUnused(c.id));
    }
    if (customer && !hasUnused(customer.id)) return [customer];
    return [];
  }, [groupCouponMode, selectedCustomers, customer, customerCoupons]);

  const openAddCouponSaleDialog = () => {
    setAddCouponSaleTypeId(assignCouponTypeIds[0] || activeCoupons[0]?.id || '');
    setAddCouponSaleError('');
    setAddCouponSaleOpen(true);
  };

  /** Record coupon sales for targets using optional preferred type / freshly assigned entries. */
  const executeCouponSale = async (targets, preferredType = null, assignedByCustomerId = null) => {
    setRecordingSale(true);
    try {
      let lastHistoryType = 'coupon';
      let couponGrand = 0;
      let creditGrand = 0;
      const itemCount = cart.length;

      for (const target of targets) {
        const customerLabel = target ? formatCustomerSaleName(target) : 'Walk-in';
        const customerName = target ? target.name : 'Walk-in';
        const baseSale = {
          customerId: target?.id || null,
          customerName: customerLabel,
          date: saleDate,
        };

        let walletGroup = null;
        let valueBudget = 0;

        const assigned = assignedByCustomerId?.[target?.id];
        if (assigned?.length) {
          const first = assigned[0];
          walletGroup = {
            couponId: first.couponId || preferredType?.id || null,
            couponName: first.couponName || preferredType?.name || 'Coupon',
            remainingValue: roundMoney(
              assigned.reduce((s, e) => s + roundMoney(e.remainingValue), 0)
            ),
            entries: assigned,
          };
          valueBudget = walletGroup.remainingValue;
        } else if (!groupCouponMode && selectedCoupon?.isWallet && selectedWalletGroup) {
          walletGroup = selectedWalletGroup;
          valueBudget = couponValue;
        } else {
          walletGroup = findWalletGroupForCustomer(target, preferredType || selectedCoupon);
          if (walletGroup) {
            valueBudget = roundMoney(walletGroup.remainingValue);
          }
        }

        if (!walletGroup || valueBudget <= 0) {
          throw new Error(
            `${customerName} has no unused coupon. Add a coupon to continue.`
          );
        }

        const { couponItems, creditItems, couponTotal, creditTotal } = splitCartByCouponValue(
          cart,
          valueBudget
        );

        const couponName = walletGroup.couponName || preferredType?.name || 'Coupon';
        const couponId = walletGroup.couponId || preferredType?.id || null;

        let allocations = [];
        if (walletGroup.entries?.length > 0 && couponTotal > 0) {
          allocations = await customerCouponService.redeemAcrossCustomerCoupons(
            walletGroup.entries,
            couponTotal
          );
        }
        const customerCouponId =
          allocations[0]?.id || walletGroup.entries?.[0]?.id || null;

        let couponSale = null;
        try {
          couponSale = await salesService.addSale({
            ...baseSale,
            items: couponItems,
            paymentType: 'coupon',
            couponId,
            couponName,
            customerCouponId,
            couponAllocations: allocations,
          });

          addActivity(
            currentUser?.userId || 'Unknown',
            `Recorded coupon sale: Nu ${couponTotal.toFixed(2)} to ${customerName} (coupon: ${couponName})`
          );

          if (creditItems.length > 0 && creditTotal > 0) {
            await salesService.addSale({
              ...baseSale,
              items: creditItems,
              paymentType: 'credit',
              couponId: null,
              couponName: null,
              saleNote: balanceAfterCouponNote(couponName, creditTotal, couponTotal),
            });
            addActivity(
              currentUser?.userId || 'Unknown',
              `Recorded credit sale: Nu ${creditTotal.toFixed(2)} to ${customerName} (credit after coupon “${couponName}” covered Nu ${couponTotal.toFixed(2)})`
            );
          }
        } catch (saleErr) {
          // Undo wallet first, then drop the coupon sale without a second restore.
          if (allocations.length > 0) {
            await customerCouponService.restoreAcrossCustomerCoupons(allocations).catch(() => {});
          }
          if (couponSale?.id) {
            await salesService
              .deleteSale(couponSale.id, { skipWalletRestore: true })
              .catch(() => {});
          }
          throw saleErr;
        }

        couponGrand = roundMoney(couponGrand + couponTotal);
        creditGrand = roundMoney(creditGrand + creditTotal);
        lastHistoryType = creditTotal > 0 ? 'credit' : 'coupon';
      }

      setHistoryDate(saleDate);
      setHistorySaleType(lastHistoryType);
      setCart([]);
      setCustomer(null);
      setSelectedCustomers([]);
      setSelectedCouponId('');
      setApplyCoupon(false);
      await refreshData();
      setSaleSuccess({
        paymentType: 'coupon',
        customerName:
          targets.length === 1
            ? targets[0]
              ? formatCustomerSaleName(targets[0])
              : 'Walk-in'
            : `${targets.length} customers`,
        amount: couponGrand,
        creditAmount: creditGrand,
        itemCount,
        customerCount: targets.length,
        date: saleDate,
      });
    } catch (err) {
      console.error('Failed to record coupon sale:', err);
      window.alert(err.message || 'Failed to record sale. Please try again.');
      await refreshData();
      throw err;
    } finally {
      setRecordingSale(false);
    }
  };

  const handleCouponSaleClick = () => {
    if (!couponsEnabled) return;
    if (cart.length === 0 || recordingSale || assigningCoupon) return;

    if (groupCouponMode) {
      if (selectedCustomers.length === 0) {
        window.alert('Select at least one customer for group coupon sale.');
        return;
      }
      if (customersNeedingCoupon.length > 0) {
        openAddCouponSaleDialog();
        return;
      }
      executeCouponSale(selectedCustomers);
      return;
    }

    if (!customer) {
      window.alert('Select a customer for coupon sale.');
      return;
    }

    if (unusedForCustomer.length === 0) {
      if (activeCoupons.length === 0) {
        window.alert('No coupon types available. Add a coupon type under Coupons first.');
        return;
      }
      openAddCouponSaleDialog();
      return;
    }

    // Has coupons: require an explicit wallet selection (Apply coupon)
    if (couponApplied && selectedWalletGroup) {
      executeCouponSale([customer]);
      return;
    }
    if (unusedCouponGroups.length === 1) {
      const only = unusedCouponGroups[0];
      const useOnly = window.confirm(
        `Use coupon “${only.couponName}” (Nu ${Number(only.remainingValue || 0).toFixed(2)} remaining)?`
      );
      if (!useOnly) return;
      executeCouponSale(
        [customer],
        { id: only.couponId, name: only.couponName },
        { [customer.id]: only.entries }
      );
      return;
    }
    window.alert(
      'Select a coupon in “Apply coupon”, then click Coupon Sale. Or choose a coupon type below first.'
    );
  };

  const handleConfirmAddCouponSale = async () => {
    const type = activeCoupons.find((c) => c.id === addCouponSaleTypeId) || null;
    if (!type) {
      setAddCouponSaleError('Select a coupon type');
      return;
    }
    const needers = customersNeedingCoupon;
    if (needers.length === 0) {
      setAddCouponSaleOpen(false);
      return;
    }

    setAddCouponSaleSaving(true);
    setAddCouponSaleError('');
    try {
      const created = await customerCouponService.assignCouponsToCustomers({
        customerIds: needers.map((c) => c.id),
        coupon: type,
      });
      addActivity(
        currentUser?.userId || 'Unknown',
        `Assigned coupon "${type.name}" to ${needers.length} customer(s) for coupon sale`
      );

      const assignedByCustomerId = {};
      created.forEach((entry) => {
        if (!assignedByCustomerId[entry.customerId]) {
          assignedByCustomerId[entry.customerId] = [];
        }
        assignedByCustomerId[entry.customerId].push(entry);
      });

      const targets = groupCouponMode ? selectedCustomers : customer ? [customer] : [];
      setAddCouponSaleOpen(false);
      await executeCouponSale(targets, type, assignedByCustomerId);
    } catch (err) {
      console.error('Add coupon sale failed:', err);
      setAddCouponSaleError(err.message || 'Failed to add coupon and record sale');
    } finally {
      setAddCouponSaleSaving(false);
    }
  };

  const handleRecordSale = async (paymentType) => {
    if (cart.length === 0 || recordingSale) return;
    if (paymentType === 'coupon') {
      handleCouponSaleClick();
      return;
    }

    if (groupCouponMode) {
      window.alert('Group mode is for coupon sales only. Turn it off for credit/cash, or use Coupon Sale.');
      return;
    }

    const customerLabel = customer ? formatCustomerSaleName(customer) : 'Walk-in';
    const customerName = customer ? customer.name : 'Walk-in';

    setRecordingSale(true);
    try {
      const itemCount = cart.length;
      const amount = totalAmount;
      await salesService.addSale({
        customerId: customer?.id || null,
        customerName: customerLabel,
        date: saleDate,
        items: cart,
        paymentType,
        couponId: null,
        couponName: null,
      });
      addActivity(
        currentUser?.userId || 'Unknown',
        `Recorded ${saleTypeLabel(paymentType).toLowerCase()} sale: Nu ${amount.toFixed(2)} to ${customerName}`
      );
      setHistoryDate(saleDate);
      setHistorySaleType(paymentType);
      setCart([]);
      setCustomer(null);
      setSelectedCustomers([]);
      setSelectedCouponId('');
      setApplyCoupon(false);
      await refreshData();
      setSaleSuccess({
        paymentType,
        customerName: customerLabel,
        amount,
        itemCount,
        customerCount: 1,
        date: saleDate,
      });
    } catch (err) {
      console.error('Failed to record sale:', err);
      window.alert(err.message || 'Failed to record sale. Please try again.');
      await refreshData();
    } finally {
      setRecordingSale(false);
    }
  };

  const handleDeleteSale = async (id, customerName, total) => {
    if (!window.confirm('Delete this sale?')) return;
    try {
      await salesService.deleteSale(id);
      addActivity(
        currentUser?.userId || 'Unknown',
        `Deleted sale: Nu ${(total || 0).toFixed(2)} (${customerName || 'Walk-in'})`
      );
      setHistorySelectedIds((prev) => prev.filter((x) => x !== id));
      refreshData();
    } catch (err) {
      window.alert(err.message || 'Failed to delete sale from database');
    }
  };

  const handleBulkDeleteHistorySales = async () => {
    if (historySelectedIds.length === 0 || historyBulkDeleting) return;
    if (
      !window.confirm(
        `Delete ${historySelectedIds.length} selected sale${historySelectedIds.length === 1 ? '' : 's'} (Nu ${historySelectedTotal.toFixed(2)})?`
      )
    ) {
      return;
    }
    setHistoryBulkDeleting(true);
    try {
      await salesService.deleteSales(historySelectedIds);
      addActivity(
        currentUser?.userId || 'Unknown',
        `Deleted ${historySelectedIds.length} ${historySaleType} sale(s) from history (Nu ${historySelectedTotal.toFixed(2)})`
      );
      setHistorySelectedIds([]);
      refreshData();
    } catch (err) {
      console.error('Failed to delete sales:', err);
      window.alert(err.message || 'Failed to delete selected sales.');
      refreshData();
    } finally {
      setHistoryBulkDeleting(false);
    }
  };

  return (
    <Box sx={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
      {salesView === 'sales' ? (
      <Stack spacing={3}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: 2.5,
            alignItems: 'stretch',
            width: '100%',
            mt: -3.5,
            ml: { xs: 0, md: -1 },
            boxSizing: 'border-box',
          }}
        >
          {/* ── New Sale ── */}
          <Paper
            variant="outlined"
            elevation={0}
            sx={{
              p: 0,
              minWidth: 0,
              width: '100%',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider',
              overflow: 'hidden',
            }}
          >
              <Box sx={{ px: 3, py: 2, bgcolor: 'primary.main', color: 'primary.contrastText' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <PointOfSaleIcon />
                  <Typography variant="h6" fontWeight={700}>
                    New Sale
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ flex: 1, overflowY: 'auto', px: 3, py: 2 }}>
                <SectionHeader
                  icon={<CalendarTodayIcon color="primary" fontSize="small" />}
                  title="Sale Details"
                  subtitle="Set date, department, and customer(s)"
                />
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={12} sm={5}>
                    <TextField
                      type="date"
                      size="small"
                      fullWidth
                      label="Sale date"
                      value={saleDate}
                      onChange={(e) => setSaleDate(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={7}>
                    <FormControl size="small" fullWidth>
                      <InputLabel id="sale-dept-label">Department</InputLabel>
                      <Select
                        labelId="sale-dept-label"
                        label="Department"
                        value={saleDept}
                        onChange={(e) => setSaleDept(e.target.value)}
                      >
                        <MenuItem value="">
                          <em>All departments</em>
                        </MenuItem>
                        {departments.map((d) => (
                          <MenuItem key={d} value={d}>
                            {d}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  {couponsEnabled && (
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={groupCouponMode}
                          onChange={(e) => setGroupCouponMode(e.target.checked)}
                        />
                      }
                      label="Group coupon sale (multi-select customers)"
                    />
                  </Grid>
                  )}
                  <Grid item xs={12}>
                    {couponsEnabled && groupCouponMode ? (
                      <Autocomplete
                        multiple
                        size="small"
                        options={customersForSale}
                        disableCloseOnSelect
                        getOptionLabel={formatCustomerOption}
                        value={selectedCustomers}
                        onChange={(_, v) => setSelectedCustomers(v)}
                        isOptionEqualToValue={(a, b) => a.id === b.id}
                        renderOption={(props, option, { selected: isSelected }) => {
                          const count = couponCountByCustomerId[option.id] || 0;
                          return (
                            <li {...props} key={option.id}>
                              <Checkbox style={{ marginRight: 8 }} checked={isSelected} />
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography variant="body2" noWrap>
                                  {formatCustomerLabel(option)}
                                </Typography>
                              </Box>
                              {couponsEnabled && (
                                <Chip
                                  size="small"
                                  label={count > 0 ? `${count} coupon${count === 1 ? '' : 's'}` : '0 coupons'}
                                  color={count > 0 ? 'success' : 'default'}
                                  variant={count > 0 ? 'filled' : 'outlined'}
                                  sx={{ ml: 1, height: 22, fontSize: '0.7rem' }}
                                />
                              )}
                            </li>
                          );
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Customers"
                            placeholder={
                              saleDept
                                ? 'Select employees for coupon sale'
                                : 'Filter department, then select'
                            }
                            InputProps={{
                              ...params.InputProps,
                              startAdornment: (
                                <>
                                  <InputAdornment position="start">
                                    <PersonIcon fontSize="small" color="action" />
                                  </InputAdornment>
                                  {params.InputProps.startAdornment}
                                </>
                              ),
                            }}
                          />
                        )}
                      />
                    ) : (
                      <Autocomplete
                        size="small"
                        options={customersForSale}
                        getOptionLabel={formatCustomerOption}
                        value={customer}
                        onChange={(_, v) => {
                          setCustomer(v);
                          setSelectedCouponId('');
                          setApplyCoupon(false);
                        }}
                        isOptionEqualToValue={(a, b) => a?.id === b?.id}
                        renderOption={(props, option) => {
                          const count = couponCountByCustomerId[option.id] || 0;
                          return (
                            <li {...props} key={option.id}>
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between',
                                  width: '100%',
                                  gap: 1,
                                }}
                              >
                                <Typography variant="body2" noWrap>
                                  {formatCustomerLabel(option)}
                                </Typography>
                                {couponsEnabled && (
                                  <Chip
                                    size="small"
                                    label={
                                      count > 0
                                        ? `${count} coupon${count === 1 ? '' : 's'}`
                                        : '0 coupons'
                                    }
                                    color={count > 0 ? 'success' : 'default'}
                                    variant={count > 0 ? 'filled' : 'outlined'}
                                    sx={{ height: 22, fontSize: '0.7rem', flexShrink: 0 }}
                                  />
                                )}
                              </Box>
                            </li>
                          );
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Customer"
                            placeholder="Walk-in"
                            InputProps={{
                              ...params.InputProps,
                              startAdornment: (
                                <>
                                  <InputAdornment position="start">
                                    <PersonIcon fontSize="small" color="action" />
                                  </InputAdornment>
                                  {params.InputProps.startAdornment}
                                </>
                              ),
                            }}
                          />
                        )}
                      />
                    )}
                  </Grid>
                </Grid>

                <Divider sx={{ mb: 2 }} />

                {couponsEnabled ? (
                <Tabs
                  value={salePanelTab}
                  onChange={(_, v) => setSalePanelTab(v)}
                  variant="fullWidth"
                  sx={{ mb: 2, minHeight: 40, borderBottom: 1, borderColor: 'divider' }}
                >
                  <Tab
                    label="Select Meals"
                    icon={<RestaurantMenuIcon fontSize="small" />}
                    iconPosition="start"
                    sx={{ minHeight: 40, textTransform: 'none', fontWeight: 600, flex: 1 }}
                  />
                  <Tab
                    label="Coupon Types"
                    icon={<LocalOfferIcon fontSize="small" />}
                    iconPosition="start"
                    sx={{ minHeight: 40, textTransform: 'none', fontWeight: 600, flex: 1 }}
                  />
                </Tabs>
                ) : (
                  <SectionHeader
                    icon={<RestaurantMenuIcon color="primary" fontSize="small" />}
                    title="Select Meals"
                    subtitle="Search or tap a meal to add"
                  />
                )}

                {(salePanelTab === 0 || !couponsEnabled) && (
                  <>
                    <TextField
                      size="small"
                      fullWidth
                      placeholder="Search meal by name or category..."
                      value={mealSearch}
                      onChange={(e) => setMealSearch(e.target.value)}
                      sx={{ mb: 2 }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon fontSize="small" color="action" />
                          </InputAdornment>
                        ),
                      }}
                    />

                    <Box sx={{ maxHeight: { xs: 260, md: 380 }, overflowY: 'auto' }}>
                      {mealsByCategory.length === 0 ? (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ py: 1.5, textAlign: 'center', display: 'block' }}
                        >
                          No active meals found
                        </Typography>
                      ) : (
                        mealsByCategory.map(([category, categoryMeals]) => (
                          <Box key={category} sx={{ mb: 1 }}>
                            <Typography
                              variant="caption"
                              fontWeight={700}
                              color="text.secondary"
                              sx={{
                                display: 'block',
                                px: 0.5,
                                mb: 0.25,
                                textTransform: 'uppercase',
                                letterSpacing: 0.5,
                              }}
                            >
                              {category}
                            </Typography>
                            <List
                              dense
                              disablePadding
                              sx={{
                                bgcolor: 'background.paper',
                                borderRadius: 1,
                                border: '1px solid',
                                borderColor: 'divider',
                              }}
                            >
                              {categoryMeals.map((meal, idx) => {
                                const inCart = cart.find((c) => c.mealId === meal.id);
                                return (
                                  <ListItem
                                    key={meal.id}
                                    disablePadding
                                    divider={idx < categoryMeals.length - 1}
                                    secondaryAction={
                                      inCart ? (
                                        <Typography
                                          variant="caption"
                                          fontWeight={700}
                                          color="primary.main"
                                          sx={{ pr: 1 }}
                                        >
                                          ×{inCart.quantity}
                                        </Typography>
                                      ) : (
                                        <AddIcon
                                          sx={{ fontSize: 16, color: 'action.active', mr: 0.5 }}
                                        />
                                      )
                                    }
                                    sx={{
                                      minHeight: 32,
                                      bgcolor: inCart ? 'action.hover' : 'transparent',
                                    }}
                                  >
                                    <ListItemButton
                                      onClick={() => addToCart(meal)}
                                      sx={{ py: 0.25, px: 1, minHeight: 32 }}
                                    >
                                      <ListItemText
                                        disableTypography
                                        primary={
                                          <Box
                                            sx={{
                                              display: 'flex',
                                              alignItems: 'center',
                                              justifyContent: 'space-between',
                                              gap: 1,
                                              pr: 4,
                                            }}
                                          >
                                            <Typography
                                              variant="body2"
                                              fontWeight={500}
                                              noWrap
                                              sx={{ fontSize: '0.8125rem' }}
                                            >
                                              {meal.name}
                                            </Typography>
                                            <Typography
                                              variant="caption"
                                              color="text.secondary"
                                              sx={{ whiteSpace: 'nowrap', fontSize: '0.75rem' }}
                                            >
                                              Nu {Number(meal.price).toFixed(2)}
                                            </Typography>
                                          </Box>
                                        }
                                      />
                                    </ListItemButton>
                                  </ListItem>
                                );
                              })}
                            </List>
                          </Box>
                        ))
                      )}
                    </Box>
                  </>
                )}

                {couponsEnabled && salePanelTab === 1 && (
                  <Box>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 1,
                        mb: 1.5,
                        flexWrap: 'wrap',
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        Select one or more coupon types, then click Assign Coupon (with customer(s)
                        selected above).
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                        {activeCoupons.length > 0 && (
                          <Button
                            size="small"
                            onClick={() =>
                              handleSelectAllAssignCouponTypes(
                                assignCouponTypeIds.length !== activeCoupons.length
                              )
                            }
                            sx={{ textTransform: 'none' }}
                          >
                            {assignCouponTypeIds.length === activeCoupons.length
                              ? 'Clear all'
                              : 'Select all'}
                          </Button>
                        )}
                        {assignCouponTypeIds.length > 0 &&
                          assignCouponTypeIds.length !== activeCoupons.length && (
                            <Button
                              size="small"
                              onClick={() => setAssignCouponTypeIds([])}
                              sx={{ textTransform: 'none' }}
                            >
                              Clear
                            </Button>
                          )}
                      </Box>
                    </Box>
                    {assignCouponTypes.length > 0 && (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1.5 }}>
                        {assignCouponTypes.map((c) => (
                          <Chip
                            key={c.id}
                            size="small"
                            label={`${c.name} · Nu ${getCouponValue(c).toFixed(2)}`}
                            color="primary"
                            onDelete={() => toggleAssignCouponType(c.id)}
                          />
                        ))}
                      </Box>
                    )}
                    {activeCoupons.length === 0 ? (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ py: 2, textAlign: 'center', display: 'block' }}
                      >
                        No coupon types yet. Add them under Coupons.
                      </Typography>
                    ) : (
                      <List
                        dense
                        disablePadding
                        sx={{
                          maxHeight: { xs: 260, md: 380 },
                          overflowY: 'auto',
                          bgcolor: 'background.paper',
                          borderRadius: 1,
                          border: '1px solid',
                          borderColor: 'divider',
                        }}
                      >
                        {activeCoupons.map((coupon, idx) => {
                          const selected = assignCouponTypeIds.includes(coupon.id);
                          const value = getCouponValue(coupon);
                          return (
                            <ListItem
                              key={coupon.id}
                              disablePadding
                              divider={idx < activeCoupons.length - 1}
                              sx={{ bgcolor: selected ? 'action.selected' : 'transparent' }}
                            >
                              <ListItemButton
                                selected={selected}
                                onClick={() => toggleAssignCouponType(coupon.id)}
                                sx={{ py: 1, px: 1.5 }}
                              >
                                <ListItemIcon sx={{ minWidth: 36 }}>
                                  <Checkbox
                                    edge="start"
                                    checked={selected}
                                    tabIndex={-1}
                                    disableRipple
                                    size="small"
                                  />
                                </ListItemIcon>
                                <ListItemText
                                  primary={coupon.name}
                                  secondary={`Qty ${coupon.quantity} @ Nu ${Number(coupon.rate).toFixed(2)} · Value Nu ${value.toFixed(2)}`}
                                  primaryTypographyProps={{ fontWeight: selected ? 700 : 500 }}
                                />
                              </ListItemButton>
                            </ListItem>
                          );
                        })}
                      </List>
                    )}
                  </Box>
                )}
              </Box>
          </Paper>

          {/* ── Cart ── */}
          <Paper
            variant="outlined"
            elevation={0}
            sx={{
              p: 0,
              minWidth: 0,
              width: '100%',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider',
              overflow: 'hidden',
            }}
          >
              <Box sx={{ px: 3, py: 2, borderBottom: 1, borderColor: 'divider' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <ShoppingCartIcon color="primary" />
                  <Box>
                    <Typography variant="h6" fontWeight={700}>
                      Cart
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {cart.length === 0 ? 'No items yet' : `${cart.length} item(s) selected`}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'auto', px: 3, py: 2, minHeight: { xs: 200, md: 280 } }}>
                {cart.length === 0 ? (
                  <Paper
                    variant="outlined"
                    sx={{ p: 3, textAlign: 'center', borderRadius: 2, borderStyle: 'dashed', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}
                  >
                    <ShoppingCartIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1, mx: 'auto' }} />
                    <Typography variant="body2" color="text.secondary">
                      Add meals from the list
                    </Typography>
                  </Paper>
                ) : (
                  <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, overflowX: 'auto' }}>
                    <Table size="small" sx={{ minWidth: 280 }}>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>Item</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 600, width: 120 }}>
                            Qty
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600, width: 120 }}>
                            Price
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {cart.map((item) => (
                          <TableRow key={item.mealId}>
                            <TableCell sx={{ fontWeight: 500 }}>{item.mealName}</TableCell>
                            <TableCell align="center">
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                                <IconButton size="small" onClick={() => adjustQuantity(item.mealId, -0.5)}>
                                  <RemoveIcon fontSize="small" />
                                </IconButton>
                                <TextField
                                  type="number"
                                  size="small"
                                  inputProps={{ min: 0.5, step: 0.5 }}
                                  sx={{ width: 56, '& input': { textAlign: 'center', py: 0.5 } }}
                                  value={item.quantity}
                                  onChange={(e) =>
                                    updateQuantity(item.mealId, parseFloat(e.target.value) || 1)
                                  }
                                />
                                <IconButton size="small" onClick={() => adjustQuantity(item.mealId, 0.5)}>
                                  <AddIcon fontSize="small" />
                                </IconButton>
                              </Box>
                            </TableCell>
                            <TableCell align="right">
                              <TextField
                                type="number"
                                size="small"
                                inputProps={{ min: 0, step: 0.01 }}
                                sx={{
                                  width: 100,
                                  '& input': { textAlign: 'right', py: 0.5, fontWeight: 600 },
                                }}
                                value={Number(item.subtotal ?? 0)}
                                onChange={(e) => updateLinePrice(item.mealId, e.target.value)}
                                InputProps={{
                                  startAdornment: (
                                    <InputAdornment position="start">
                                      <Typography variant="caption" color="text.secondary">
                                        Nu
                                      </Typography>
                                    </InputAdornment>
                                  ),
                                }}
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}

                {/* Customer unused coupon details */}
                {couponsEnabled && !groupCouponMode && customer && (
                  <Box
                    sx={{
                      mt: 2,
                      p: 1.5,
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      bgcolor: 'background.paper',
                    }}
                  >
                    <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                      {customer.name}&apos;s coupons
                    </Typography>
                    {unusedCouponGroups.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">
                        No coupon for this customer
                      </Typography>
                    ) : (
                      <Stack spacing={0.75}>
                        {unusedCouponGroups.map((g) => (
                          <Box
                            key={g.key}
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              gap: 1,
                            }}
                          >
                            <Typography variant="body2">
                              {g.couponName}
                              <Typography
                                component="span"
                                variant="caption"
                                color="text.secondary"
                                display="block"
                              >
                                {g.count} coupon{g.count === 1 ? '' : 's'} · Qty {g.quantity} @ Nu{' '}
                                {Number(g.rate).toFixed(2)}
                              </Typography>
                            </Typography>
                            <Chip
                              size="small"
                              color="success"
                              label={`${g.count} × · Nu ${roundMoney(g.remainingValue).toFixed(2)}`}
                            />
                          </Box>
                        ))}
                        <Typography variant="caption" color="text.secondary">
                          Total unused: Nu{' '}
                          {(couponBalanceByCustomerId[customer.id] || 0).toFixed(2)} (
                          {unusedForCustomer.length} coupon
                          {unusedForCustomer.length === 1 ? '' : 's'})
                        </Typography>
                      </Stack>
                    )}
                  </Box>
                )}
              </Box>

              <Box
                sx={{
                  px: 3,
                  py: 2,
                  borderTop: 1,
                  borderColor: 'divider',
                  bgcolor: 'action.hover',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1.5,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant="caption" color="text.secondary">
                    Order total
                  </Typography>
                  <Typography variant="h5" fontWeight={800} color="primary.main">
                    Nu {totalAmount.toFixed(2)}
                  </Typography>
                </Box>

                {couponsEnabled && !groupCouponMode && customer && (
                  <>
                    {unusedForCustomer.length === 0 ? (
                      <Typography variant="caption" color="warning.main">
                        No coupon for this customer — click Coupon Sale to add one and complete the
                        sale.
                      </Typography>
                    ) : (
                      <>
                        <FormControl size="small" fullWidth>
                          <InputLabel id="cart-coupon-label">Apply customer coupon</InputLabel>
                          <Select
                            labelId="cart-coupon-label"
                            label="Apply customer coupon"
                            value={selectedCouponId}
                            onChange={(e) => {
                              setSelectedCouponId(e.target.value);
                              if (!e.target.value) setApplyCoupon(false);
                              else setApplyCoupon(true);
                            }}
                          >
                            <MenuItem value="">
                              <em>None</em>
                            </MenuItem>
                            {unusedCouponGroups.map((g) => (
                              <MenuItem key={`group:${g.key}`} value={`group:${g.key}`}>
                                {g.couponName} · {g.count} coupon{g.count === 1 ? '' : 's'} — Nu{' '}
                                {roundMoney(g.remainingValue).toFixed(2)} left
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={applyCoupon}
                              onChange={(e) => setApplyCoupon(e.target.checked)}
                              disabled={!selectedCouponId}
                            />
                          }
                          label="Apply coupon for free item(s)"
                        />
                        {couponApplied && selectedCoupon && (
                          <Alert
                            severity={couponSplit.hasCreditBalance ? 'info' : 'success'}
                            sx={{ py: 0.75 }}
                          >
                            <Typography variant="body2" fontWeight={700} gutterBottom>
                              Coupon “{selectedCoupon.name}”
                              {selectedCoupon.count > 1 ? ` (${selectedCoupon.count}×)` : ''}
                            </Typography>
                            <Typography variant="body2" component="div">
                              Available: Nu {couponValue.toFixed(2)}
                              {couponSplit.hasCreditBalance ? (
                                <>
                                  <br />
                                  Coupon covers: <strong>Nu {couponSplit.couponTotal.toFixed(2)}</strong>
                                  <br />
                                  Remaining charged to credit:{' '}
                                  <strong>Nu {couponSplit.creditTotal.toFixed(2)}</strong>
                                </>
                              ) : (
                                <>
                                  <br />
                                  Full cart covered by coupon — no credit charge.
                                </>
                              )}
                            </Typography>
                          </Alert>
                        )}
                      </>
                    )}
                  </>
                )}

                {couponsEnabled && groupCouponMode && (
                  <Typography variant="caption" color="text.secondary">
                    Group coupon sale uses each selected customer&apos;s unused coupons. Assign coupons
                    first if needed.
                    {assignCouponTypes.length > 0
                      ? ` Selected for assign: ${assignCouponTypes.map((t) => t.name).join(', ')}.`
                      : ''}
                  </Typography>
                )}

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} flexWrap="wrap" useFlexGap>
                  <Button
                    variant="contained"
                    size="large"
                    fullWidth
                    color="primary"
                    startIcon={<CreditCardIcon />}
                    onClick={() => handleRecordSale('credit')}
                    disabled={
                      cart.length === 0 ||
                      recordingSale ||
                      (couponsEnabled && groupCouponMode) ||
                      assigningCoupon
                    }
                    sx={{ py: 1.25, flex: { sm: '1 1 0' } }}
                  >
                    Credit Sale
                  </Button>
                  <Button
                    variant="contained"
                    size="large"
                    fullWidth
                    color="success"
                    startIcon={<NuIcon />}
                    onClick={() => handleRecordSale('cash')}
                    disabled={
                      cart.length === 0 ||
                      recordingSale ||
                      (couponsEnabled && groupCouponMode) ||
                      assigningCoupon
                    }
                    sx={{ py: 1.25, flex: { sm: '1 1 0' } }}
                  >
                    Cash Sale
                  </Button>
                  {couponsEnabled && (
                    <>
                  <Button
                    variant="contained"
                    size="large"
                    fullWidth
                    color="warning"
                    startIcon={<LocalOfferIcon />}
                    onClick={() => handleRecordSale('coupon')}
                    disabled={
                      cart.length === 0 ||
                      recordingSale ||
                      assigningCoupon ||
                      addCouponSaleSaving ||
                      (groupCouponMode ? selectedCustomers.length === 0 : !customer)
                    }
                    sx={{
                      py: 1.25,
                      flex: { sm: '1 1 0' },
                      bgcolor: '#F5C518',
                      color: '#1a1a1a',
                      '&:hover': { bgcolor: '#E0B000' },
                      '&.Mui-disabled': {
                        bgcolor: 'action.disabledBackground',
                        color: 'action.disabled',
                      },
                    }}
                  >
                    {groupCouponMode
                      ? `Coupon Sale (${selectedCustomers.length})`
                      : 'Coupon Sale'}
                  </Button>
                  <Button
                    variant="outlined"
                    size="large"
                    fullWidth
                    color="secondary"
                    startIcon={<LocalOfferIcon />}
                    onClick={handleAssignCoupon}
                    disabled={
                      assigningCoupon ||
                      recordingSale ||
                      assignCouponTypes.length === 0 ||
                      (groupCouponMode ? selectedCustomers.length === 0 : !customer)
                    }
                    sx={{ py: 1.25, flex: { sm: '1 1 0' } }}
                  >
                    {assigningCoupon
                      ? 'Assigning…'
                      : assignCouponTypes.length > 1
                        ? `Assign Coupons (${assignCouponTypes.length})`
                        : 'Assign Coupon'}
                  </Button>
                    </>
                  )}
                </Stack>
              </Box>
          </Paper>
        </Box>
      </Stack>
      ) : (
        <Box sx={{ mt: { xs: -1, md: -2 } }}>
          <Paper
            sx={{
              p: 0,
              display: 'flex',
              flexDirection: 'column',
              borderRadius: 3,
              overflow: 'hidden',
              minHeight: { xs: 480, md: 'calc(100vh - 160px)' },
            }}
          >
            <Box sx={{ px: 3, py: 2, borderBottom: 1, borderColor: 'divider' }}>
              <Tabs
                value={historySaleType}
                onChange={(_, value) => setHistorySaleType(value)}
                sx={{ mb: 2, minHeight: 40, borderBottom: 1, borderColor: 'divider' }}
              >
                <Tab
                  value="credit"
                  label="Credit Sales"
                  icon={<CreditCardIcon fontSize="small" />}
                  iconPosition="start"
                  sx={{ minHeight: 40, textTransform: 'none', fontWeight: 600 }}
                />
                <Tab
                  value="cash"
                  label="Cash Sales"
                  icon={<NuIcon fontSize="small" />}
                  iconPosition="start"
                  sx={{ minHeight: 40, textTransform: 'none', fontWeight: 600 }}
                />
                {couponsEnabled && (
                <Tab
                  value="coupon"
                  label="Coupon Sales"
                  icon={<LocalOfferIcon fontSize="small" />}
                  iconPosition="start"
                  sx={{ minHeight: 40, textTransform: 'none', fontWeight: 600 }}
                />
                )}
              </Tabs>

              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={4} md={3}>
                  <TextField
                    type="date"
                    size="small"
                    fullWidth
                    label="Filter by date"
                    value={historyDate}
                    onChange={(e) => setHistoryDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={8} md={5}>
                  <TextField
                    size="small"
                    fullWidth
                    placeholder="Search customer or amount..."
                    value={historySearch}
                    onChange={(e) => setHistorySearch(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" color="action" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
                    <Chip
                      label={`${historySummary.count} sale${historySummary.count !== 1 ? 's' : ''}`}
                      color="primary"
                      variant="outlined"
                      size="small"
                    />
                    <Chip
                      label={`Nu ${historySummary.total.toFixed(2)}`}
                      color="success"
                      variant="outlined"
                      size="small"
                    />
                    {historySelectedIds.length > 0 && (
                      <>
                        <Chip
                          label={`${historySelectedIds.length} selected · Nu ${historySelectedTotal.toFixed(2)}`}
                          color="warning"
                          variant="outlined"
                          size="small"
                          onDelete={() => setHistorySelectedIds([])}
                        />
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          startIcon={<DeleteSweepIcon />}
                          onClick={handleBulkDeleteHistorySales}
                          disabled={historyBulkDeleting}
                          sx={{ textTransform: 'none' }}
                        >
                          {historyBulkDeleting
                            ? 'Deleting…'
                            : `Delete selected (${historySelectedIds.length})`}
                        </Button>
                      </>
                    )}
                  </Stack>
                </Grid>
              </Grid>
            </Box>

            <TableContainer sx={{ flex: 1 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox" sx={{ bgcolor: 'background.paper' }}>
                      <Checkbox
                        indeterminate={
                          historySelectedIds.length > 0 &&
                          historySelectedIds.length < filteredSales.length
                        }
                        checked={
                          filteredSales.length > 0 &&
                          historySelectedIds.length === filteredSales.length
                        }
                        onChange={handleHistorySelectAll}
                        disabled={filteredSales.length === 0}
                      />
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, bgcolor: 'background.paper' }}>Time</TableCell>
                    <TableCell sx={{ fontWeight: 700, bgcolor: 'background.paper' }}>Customer</TableCell>
                    <TableCell sx={{ fontWeight: 700, bgcolor: 'background.paper' }} align="center">
                      Items
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, bgcolor: 'background.paper' }} align="right">
                      Total
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, bgcolor: 'background.paper', width: 56 }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredSales.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 10 }}>
                        <HistoryIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                        <Typography color="text.secondary">
                          {historySearch.trim()
                            ? 'No sales match your search'
                            : `No ${historySaleType} sales on this date`}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                          Record a {historySaleType} sale to see it here
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredSales.map((sale) => (
                      <TableRow
                        key={sale.id}
                        hover
                        selected={historySelectedIds.includes(sale.id)}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={historySelectedIds.includes(sale.id)}
                            onChange={() => handleHistorySelectOne(sale.id)}
                          />
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          {sale.createdAt
                            ? new Date(sale.createdAt).toLocaleTimeString([], {
                                hour: '2-digit',
                                minute: '2-digit',
                              })
                            : '—'}
                        </TableCell>
                        <TableCell
                          sx={{
                            cursor: 'pointer',
                            color: 'primary.main',
                            fontWeight: 500,
                            '&:hover': { textDecoration: 'underline' },
                          }}
                          onClick={() => {
                            setSelectedSale(sale);
                            setSelectedItemIndices(new Set());
                          }}
                        >
                          {sale.customerName}
                          <SaleAnnotations sale={sale} />
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={(sale.items || []).length}
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>
                          Nu {(sale.totalAmount || 0).toFixed(2)}
                        </TableCell>
                        <TableCell align="right">
                          <IconButton
                            size="small"
                            onClick={() =>
                              handleDeleteSale(sale.id, sale.customerName, sale.totalAmount)
                            }
                            color="error"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Box>
      )}

      {/* Add coupon then complete coupon sale */}
      <Dialog
        open={couponsEnabled && addCouponSaleOpen}
        onClose={() => !addCouponSaleSaving && setAddCouponSaleOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Add Coupon for Sale</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {customersNeedingCoupon.length === 1
              ? `${customersNeedingCoupon[0].name} has no unused coupon. Select a coupon type to assign, then complete the coupon sale.`
              : `${customersNeedingCoupon.length} selected customer(s) have no unused coupon. Select a coupon type to assign to them, then complete the coupon sale.`}
          </Typography>
          {addCouponSaleError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {addCouponSaleError}
            </Alert>
          )}
          <FormControl fullWidth size="small">
            <InputLabel id="add-coupon-sale-type-label">Coupon type</InputLabel>
            <Select
              labelId="add-coupon-sale-type-label"
              label="Coupon type"
              value={addCouponSaleTypeId}
              onChange={(e) => setAddCouponSaleTypeId(e.target.value)}
            >
              {activeCoupons.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.name} — Nu {getCouponValue(c).toFixed(2)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {customersNeedingCoupon.length > 0 && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1.5 }}>
              Will assign to:{' '}
              {customersNeedingCoupon.map((c) => c.name).join(', ')}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddCouponSaleOpen(false)} disabled={addCouponSaleSaving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmAddCouponSale}
            disabled={addCouponSaleSaving || !addCouponSaleTypeId || activeCoupons.length === 0}
          >
            {addCouponSaleSaving ? 'Saving…' : 'Add coupon & record sale'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Order detail dialog */}
      <Dialog
        open={!!selectedSale}
        onClose={() => {
          setSelectedSale(null);
          setSelectedItemIndices(new Set());
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <Box>
            <Typography variant="h6" fontWeight={700}>
              Order Details
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {selectedSale?.customerName} · {selectedSale?.date} ·{' '}
              {saleTypeLabel(getSaleType(selectedSale))}
              {selectedSale?.couponName ? ` · Coupon: ${selectedSale.couponName}` : ''}
            </Typography>
            {selectedSale?.saleNote ? (
              <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                {selectedSale.saleNote}
              </Typography>
            ) : null}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {selectedItemIndices.size > 0 && (
              <Button
                variant="outlined"
                color="error"
                size="small"
                startIcon={<DeleteSweepIcon />}
                onClick={handleRemoveMultipleItemsFromSale}
              >
                Delete ({selectedItemIndices.size})
              </Button>
            )}
            <IconButton
              size="small"
              onClick={() => {
                setSelectedSale(null);
                setSelectedItemIndices(new Set());
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {selectedSale && (
            <>
              <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox" sx={{ width: 48 }}>
                        <Checkbox
                          indeterminate={
                            selectedItemIndices.size > 0 &&
                            selectedItemIndices.size < (selectedSale.items || []).length
                          }
                          checked={
                            (selectedSale.items || []).length > 0 &&
                            selectedItemIndices.size === (selectedSale.items || []).length
                          }
                          onChange={toggleSelectAllItems}
                        />
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Item</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 600 }}>Qty</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>Unit Price</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>Subtotal</TableCell>
                      <TableCell padding="none" sx={{ width: 48 }} />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(selectedSale.items || []).length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                          No items in this order
                        </TableCell>
                      </TableRow>
                    ) : (
                      (selectedSale.items || []).map((item, idx) => (
                        <TableRow key={idx} selected={selectedItemIndices.has(idx)}>
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={selectedItemIndices.has(idx)}
                              onChange={() => toggleItemSelection(idx)}
                            />
                          </TableCell>
                          <TableCell>{item.mealName}</TableCell>
                          <TableCell align="center">{item.quantity}</TableCell>
                          <TableCell align="right">Nu {(item.unitPrice || 0).toFixed(2)}</TableCell>
                          <TableCell align="right">
                            Nu {(item.subtotal ?? (item.quantity * item.unitPrice || 0)).toFixed(2)}
                          </TableCell>
                          <TableCell padding="none">
                            <IconButton
                              size="small"
                              onClick={() => handleRemoveItemFromSale(selectedSale, idx)}
                              color="error"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <Box
                sx={{
                  mt: 2,
                  p: 2,
                  borderRadius: 2,
                  bgcolor: 'action.hover',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: 2,
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Order total
                </Typography>
                <Typography
                  variant="h6"
                  fontWeight={800}
                  color={
                    (selectedSale.totalAmount || 0) < 0 ? 'success.main' : 'primary.main'
                  }
                >
                  Nu {(selectedSale.totalAmount || 0).toFixed(2)}
                </Typography>
              </Box>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Sale completed confirmation */}
      <Dialog
        open={!!saleSuccess}
        onClose={() => setSaleSuccess(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogContent sx={{ pt: 4, pb: 2, textAlign: 'center' }}>
          <CheckCircleOutlineIcon
            sx={{ fontSize: 64, color: 'success.main', mb: 1.5 }}
          />
          <Typography variant="h6" fontWeight={800} gutterBottom>
            Sale recorded
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
            Transaction completed successfully
          </Typography>

          {saleSuccess && (
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                borderRadius: 2,
                textAlign: 'left',
                bgcolor: 'action.hover',
              }}
            >
              <Stack spacing={1}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Payment
                  </Typography>
                  <Chip
                    size="small"
                    label={saleTypeLabel(saleSuccess.paymentType)}
                    color={
                      saleSuccess.paymentType === 'cash'
                        ? 'success'
                        : saleSuccess.paymentType === 'coupon'
                          ? 'warning'
                          : 'primary'
                    }
                    variant="outlined"
                  />
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Customer
                  </Typography>
                  <Typography variant="body2" fontWeight={600} textAlign="right">
                    {saleSuccess.customerName}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Date
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {saleSuccess.date}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Items
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {saleSuccess.itemCount}
                    {saleSuccess.customerCount > 1
                      ? ` × ${saleSuccess.customerCount} customers`
                      : ''}
                  </Typography>
                </Box>
                {saleSuccess.paymentType === 'coupon' ? (
                  <>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        Coupon covered
                      </Typography>
                      <Typography variant="body2" fontWeight={700} color="warning.dark">
                        Nu {(saleSuccess.amount || 0).toFixed(2)}
                      </Typography>
                    </Box>
                    {(saleSuccess.creditAmount || 0) > 0 && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                          Credit remainder
                        </Typography>
                        <Typography variant="body2" fontWeight={700} color="primary.main">
                          Nu {saleSuccess.creditAmount.toFixed(2)}
                        </Typography>
                      </Box>
                    )}
                  </>
                ) : (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Total
                    </Typography>
                    <Typography variant="h6" fontWeight={800} color="primary.main">
                      Nu {(saleSuccess.amount || 0).toFixed(2)}
                    </Typography>
                  </Box>
                )}
              </Stack>
            </Paper>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 1, gap: 1, justifyContent: 'center' }}>
          <Button
            variant="outlined"
            startIcon={<HistoryIcon />}
            onClick={() => {
              const type =
                saleSuccess?.paymentType === 'coupon' && (saleSuccess?.creditAmount || 0) > 0
                  ? 'credit'
                  : saleSuccess?.paymentType || 'credit';
              setHistorySaleType(type === 'coupon' || type === 'cash' || type === 'credit' ? type : 'credit');
              if (saleSuccess?.date) setHistoryDate(saleSuccess.date);
              setSaleSuccess(null);
              navigate('/sales?tab=history');
            }}
          >
            View history
          </Button>
          <Button variant="contained" onClick={() => setSaleSuccess(null)} autoFocus>
            Done
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Sales;
